import { useState, useEffect, useRef } from 'react';

const faqs = [
  {
    q: 'Where can I buy quality furniture near me in Las Vegas?',
    a: 'Eclectic Home is a premium furniture store located in Las Vegas, NV. We carry 500+ handpicked pieces across living room, bedroom, dining room, home office, and outdoor collections. Visit our showroom for a personalized design consultation, or browse our collections online.',
  },
  {
    q: 'What is the best furniture for my home?',
    a: 'The best furniture depends on your space, lifestyle, and aesthetic. We recommend starting with a hero piece — a sofa, bed frame, or dining table — then building around it. Our free design consultation helps you identify what works best for your specific rooms and how you live.',
  },
  {
    q: 'How much does quality furniture cost?',
    a: 'Premium furniture varies widely. Our accent chairs start around $800, sofas from $1,800, dining tables from $1,200, and bed frames from $1,500. We believe in transparent pricing and carry options across a range of budgets. All pieces include free delivery and professional setup.',
  },
  {
    q: 'How do I choose furniture for my space?',
    a: 'Start with measurements of your room and doorways. Think about flow, natural light, and how you\'ll use the space. Our design consultants guide you through this process — it\'s included free with every showroom visit. We also offer virtual consultations for your convenience.',
  },
  {
    q: 'What furniture does Eclectic Home offer?',
    a: 'We offer curated collections for every room in your home: living room sofas, sectionals, and coffee tables; bedroom frames, nightstands, and dressers; dining tables and chairs; home office desks and storage; and weather-resistant outdoor furniture. All pieces are selected for quality and design.',
  },
  {
    q: 'Do you offer custom or made-to-order furniture?',
    a: 'Yes. Many of our pieces are available in custom upholstery, finishes, and dimensions. Lead times vary from 6–14 weeks for custom orders. Ask our design team about customization options during your showroom visit or consultation.',
  },
  {
    q: 'What is your delivery and setup process?',
    a: 'We offer white-glove delivery and professional in-home assembly on all orders. Our team delivers on your schedule, places furniture exactly where you want it, and removes all packaging. There are no surprises — just your new furniture, perfectly set up.',
  },
];

export default function FaqSection() {
  const [open, setOpen] = useState<number | null>(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="py-28 bg-cream-mid" itemScope itemType="https://schema.org/FAQPage">
      <div ref={ref} className="fade-up max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Common Questions</span>
            <span className="w-8 h-px bg-gold" />
          </div>
          <h2 className="font-serif text-5xl font-bold text-dark mb-4">
            Frequently Asked<br />Questions
          </h2>
          <p className="text-dark/50 text-sm max-w-md mx-auto">
            Everything you need to know about our <strong>furniture store near you</strong> and how we work.
          </p>
        </div>

        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {faqs.map((faq, i) => (
            <div
              key={i}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className={`rounded-xl overflow-hidden border transition-colors ${open === i ? 'border-gold/40 bg-white' : 'border-cream-dark bg-white/60 hover:border-gold/20'}`}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
              >
                <h3 itemProp="name" className={`font-semibold pr-4 text-sm leading-relaxed transition-colors ${open === i ? 'text-dark' : 'text-dark/70'}`}>
                  {faq.q}
                </h3>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${open === i ? 'bg-gold text-white' : 'bg-cream-dark text-dark/50'}`}>
                  <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                </div>
              </button>
              {open === i && (
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-6">
                  <p itemProp="text" className="text-dark/60 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
